import './setupAuthHeaders'; // side-effect: attach Bearer token to raw axios/fetch calls (must run first)
import React from 'react';
import ReactDOM from 'react-dom/client'; // Import from 'react-dom/client'
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'react-jss';
import Theme from './resources/theme';
import Routes from './routes';
import './index.css';
import * as serviceWorker from './serviceWorker';
import { AuthProvider } from './context/logeduser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GlobalStateProvider } from './context/empcontext';
import { LeaveProvider } from './context/leavecontext';
import { AlertProvider } from './context/AlertContext';
import { DeleteConfirmationProvider } from './context/DeleteConfirmationContext';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import muiTheme from './theme/muiTheme';
import { LoaderProvider } from './components/common/GlobalLoader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CompanyProvider, getCompanyCode, isSuperAdminPath } from './context/CompanyContext';
import { CompanyModulesProvider } from './context/CompanyModulesContext';
import SuperAdminApp from './components/superadmin/SuperAdminApp';

const queryClient = new QueryClient();

// Suppress ResizeObserver loop limit exceeded error
const resizeObserverLoopErr = 'ResizeObserver loop completed with undelivered notifications.';
window.addEventListener('error', (e) => {
  if (e.message === resizeObserverLoopErr || e.message === 'ResizeObserver loop limit exceeded') {
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.style.display = 'none';
    }
    e.stopImmediatePropagation();
  }
});

const companyCode = getCompanyCode();
const basename = companyCode ? `/${companyCode}` : '/';
const root = ReactDOM.createRoot(document.getElementById('root'));

if (isSuperAdminPath()) {
  root.render(
    <MuiThemeProvider theme={muiTheme}>
      <Router basename="/superadmin">
        <SuperAdminApp />
      </Router>
    </MuiThemeProvider>
  );
} else {
  root.render(
    <MuiThemeProvider theme={muiTheme}>
      <LoaderProvider>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ zIndex: 99999 }}
        />
        <ThemeProvider theme={Theme}>
          <AlertProvider>
            <QueryClientProvider client={queryClient}>
              <DeleteConfirmationProvider>
                <GlobalStateProvider>
                  <LeaveProvider>
                    <CompanyProvider>
                      <AuthProvider>
                        <CompanyModulesProvider>
                          <Router basename={basename}>
                            <Routes />
                          </Router>
                        </CompanyModulesProvider>
                      </AuthProvider>
                    </CompanyProvider>
                  </LeaveProvider>
                </GlobalStateProvider>
              </DeleteConfirmationProvider>
            </QueryClientProvider>
          </AlertProvider>
        </ThemeProvider>
      </LoaderProvider>
    </MuiThemeProvider>
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
